import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { NumberTicker } from '@/components/hud/hud';

type Tone = 'star' | 'ember' | 'green' | 'violet' | 'rose' | 'gold';

const TONE_COLOR: Record<Tone, string> = {
  star: 'var(--star)',
  ember: 'var(--ember)',
  green: 'var(--p-arcadia)',
  violet: 'var(--p-pyro)',
  rose: '#f06d8a',
  gold: 'var(--p-elysion)',
};

export interface MissionSignal {
  label: string;
  value: ReactNode;
  tone?: Tone;
}

/** ViewBar 지표·필터 줄에 쓰는 라벨+값 칩. */
export function SignalPill({
  label,
  value,
  tone = 'star',
  className,
}: MissionSignal & { className?: string }) {
  const color = TONE_COLOR[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-line bg-panel/50 px-2.5 py-1 text-xs text-dim',
        className,
      )}
    >
      <span className="size-1.5 rounded-full" style={{ background: color }} />
      <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-faint">{label}</span>
      <span className="text-ink">
        {typeof value === 'number' ? <NumberTicker value={value} /> : value}
      </span>
    </span>
  );
}

/** 분석↔합일 등 0..1 좌표를 점으로 찍는 미니 게이지 (세계관 행성 축). */
export function MiniMeter({
  value,
  left = '분석',
  right = '합일',
  color = 'var(--star)',
}: {
  value: number;
  left?: string;
  right?: string;
  color?: string;
}) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div>
      <div className="relative h-2 rounded-full bg-line">
        <div
          className="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ left: `${pct}%`, background: color, boxShadow: `0 0 14px ${color}` }}
        />
      </div>
      <div className="mt-1 flex justify-between font-mono text-[10px] uppercase tracking-[0.12em] text-fainter">
        <span>{left}</span>
        <span>{right}</span>
      </div>
    </div>
  );
}

/**
 * 세그먼트 핍 — n/total을 셀 수 있는 칸으로(FTL 시스템 바). 진행/완성도를 "계기"로.
 */
export function Pips({
  filled,
  total,
  color = 'var(--star)',
  className,
}: {
  filled: number;
  total: number;
  color?: string;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center gap-1', className)} role="meter" aria-valuenow={filled} aria-valuemax={total}>
      {Array.from({ length: Math.max(0, total) }).map((_, i) => {
        const on = i < filled;
        return (
          <span
            key={i}
            className="h-2.5 flex-1 rounded-[1px] transition-colors"
            style={{
              minWidth: 4,
              background: on ? color : 'var(--line-2)',
              opacity: on ? 1 : 0.5,
              boxShadow: on ? `0 0 6px ${color}88` : 'none',
            }}
          />
        );
      })}
    </div>
  );
}

/**
 * 클록 링 — 원형 세그먼트 진행(Citizen Sleeper). value 0..1, segments칸.
 * SVG stroke-dasharray로 칸 분리. 비트 완성·아크 진행 등.
 */
export function ClockRing({
  value,
  segments = 8,
  size = 36,
  color = 'var(--star)',
  className,
}: {
  value: number;
  segments?: number;
  size?: number;
  color?: string;
  className?: string;
}) {
  const v = Math.max(0, Math.min(1, value));
  const filled = Math.round(v * segments);
  const r = size / 2 - 3;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  const gap = circ * 0.04;
  const seg = circ / segments - gap;
  return (
    <svg width={size} height={size} className={className} role="meter" aria-valuenow={filled} aria-valuemax={segments}>
      <g transform={`rotate(-90 ${cx} ${cy})`}>
        {Array.from({ length: segments }).map((_, i) => {
          const on = i < filled;
          return (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={on ? color : 'var(--line-2)'}
              strokeWidth={3}
              strokeOpacity={on ? 1 : 0.45}
              strokeDasharray={`${seg} ${circ - seg}`}
              strokeDashoffset={-i * (circ / segments)}
              style={on ? { filter: `drop-shadow(0 0 3px ${color})` } : undefined}
            />
          );
        })}
      </g>
    </svg>
  );
}
